import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import { type CSSProperties, type MouseEvent, useEffect, useMemo, useState } from 'react'
import {
  type SaleDeliveryOrder,
  useDeleteSaleDeliveryOrderMutation,
  useGetSaleDeliveryOrderCanHardDeleteQuery,
  useGetSaleDeliveryOrdersQuery,
  useLazyGetSaPrintPreviewQuery,
  useLazyGetDoPrintPreviewQuery,
  useLazyGetDispatchPrintFormQuery,
  useLazyGetDispatchPrintPreviewQuery,
} from '../../store/api/saleDeliveryOrderApi'
import SaPrintDialog from './SaPrintDialog'
import DoPrintDialog from './DoPrintDialog'
import DispatchPrintDialog from './DispatchPrintDialog'
import DispatchEditDialog from './DispatchEditDialog'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import DeliveryOrderFormDialog from './DeliveryOrderFormDialog'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '../../components/dataList'
import ResizeHandle from '../../components/ResizeHandle'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { useIsMobile } from '../../hooks/useResponsive'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'

type FilterKind = 'search'
type FinalizedFilter = 'all' | 'yes' | 'no'

/** Freeze the DO No. identifier column when scrolling horizontally (skill /table-style §7.6). */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['doNo'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { doNo: 110 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { doNo: 100 }

/** TablePagination «All» maps to this pageSize (single request). */
const ALL_ROWS_PAGE_SIZE = 1_000_000

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = {
  key: keyof SaleDeliveryOrder
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  wrap?: boolean
  width?: number
}

/**
 * Column order + captions follow Access `frmCtrlSalesQryDO` datasheet (ColumnOrder
 * + Label.Caption); date format `d-mmm-yy`, numbers `Standard`/2dp right-aligned.
 */
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'id', label: 'ID', kind: 'number', align: 'right' },
  { key: 'fromWarehouse', label: 'From WH' },
  { key: 'saleContract', label: 'For S' },
  { key: 'buyersRef', label: 'Buyer ref.' },
  { key: 'doNo', label: 'DO No.' },
  { key: 'doDate', label: 'DO date', kind: 'date' },
  { key: 'loadingDate', label: 'Loading date', kind: 'date' },
  { key: 'accumulateDelivered', label: 'Accumulate delivered', kind: 'number', align: 'right' },
  { key: 'pending', label: 'Pending', kind: 'number', align: 'right' },
  { key: 'nwKgs', label: 'NW kgs', kind: 'number', align: 'right' },
  { key: 'buyerName', label: "Buyers' Names" },
  { key: 'contractWeightKg', label: 'Contract weight (kgs)', kind: 'number', align: 'right' },
  { key: 'quality', label: 'Quality', filter: 'checkboxes' },
  { key: 'certification', label: 'Certification', filter: 'checkboxes' },
  { key: 'deliveryTerms', label: 'Delivery Terms', filter: 'checkboxes' },
  { key: 'contractWarehouse', label: 'Contract Warehouse' },
  { key: 'shippingMonth', label: 'Shipping From', kind: 'date' },
  { key: 'shippingTo', label: 'Shipping To', kind: 'date' },
  { key: 'packaging', label: 'Package' },
  { key: 'icoNo', label: 'ICO No.' },
  { key: 'marking', label: 'Marking', wrap: true, width: 200 },
  { key: 'dunnage', label: 'Dunnage', wrap: true, width: 220 },
  { key: 'typeOfInspection', label: 'Type of inspection', filter: 'checkboxes' },
  { key: 'inspectionName', label: 'Inspection Co.' },
  { key: 'fumiDose', label: 'Fumi Dose' },
  { key: 'fumigationName', label: 'Fumigation Co.' },
  { key: 'noContTruckNum', label: 'No. cont/truck', kind: 'number', align: 'right' },
  { key: 'truckName', label: 'Truck Co.' },
  { key: 'customs', label: 'Customs Code' },
  { key: 'customsName', label: 'Customs Name' },
  { key: 'bookingNote', label: 'Note', wrap: true, width: 220 },
  { key: 'bookingNo', label: 'Booking No.' },
  { key: 'freightPayable', label: 'Freight payable' },
  { key: 'blNumber', label: 'B/L number' },
  { key: 'shippingLinesName', label: 'Shipping line' },
  { key: 'vesselNameVoyage', label: 'Vessel Name and Voyage' },
  { key: 'exportTerminalName', label: 'Export terminal' },
  { key: 'internalTransshipmentPort', label: 'Internal Transit Port' },
  { key: 'portOfLoadingCode', label: 'Port of loading (Code)' },
  { key: 'portOfLoadingName', label: 'Port of loading' },
  { key: 'transhipmentPortCode', label: 'Transhipment port (Code)' },
  { key: 'transhipmentPortName', label: 'Transhipment port' },
  { key: 'destinationPortCode', label: 'Destination port (Code)' },
  { key: 'destinationPortName', label: 'Destination port' },
  { key: 'placeOfDelivery', label: 'Place of delivery' },
  { key: 'cutOff', label: 'Cut-off' },
  { key: 'etd', label: 'ETD', kind: 'date' },
  { key: 'eta', label: 'ETA', kind: 'date' },
  { key: 'atd', label: 'ATD', kind: 'date' },
  { key: 'docsPresentationDate', label: 'Docs presentation date', kind: 'date' },
  { key: 'sendingDocsTo', label: 'Sending docs to' },
  { key: 'indication', label: 'Indication' },
  { key: 'docsReceiverFullname', label: 'Docs receiver full name' },
  { key: 'fullName', label: 'PIC' },
  { key: 'dateUpdate', label: 'Date Updated', kind: 'datetime' },
  { key: 'personUpdate', label: 'Person Updated' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const sumNumericColumn = (rows: SaleDeliveryOrder[], key: keyof SaleDeliveryOrder): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

interface SaleControlDeliveryOrderViewProps {
  shipmentPeriodStart?: string
  shipmentPeriodEnd?: string
  finalizedFilter?: FinalizedFilter
}

export default function SaleControlDeliveryOrderView({
  shipmentPeriodStart = '',
  shipmentPeriodEnd = '',
  finalizedFilter = 'all',
}: SaleControlDeliveryOrderViewProps) {
  const { canEdit } = usePagePermission()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP
  const snack = useSnackbar()
  const { selectedId, setSelectedId, toggleSelect } = useRowSelection<number>()
  const [confirmDelete, setConfirmDelete] = useState<SaleDeliveryOrder | null>(null)
  const [formState, setFormState] = useState<{ mode: 'new' | 'edit' | 'copy'; doId: number | null } | null>(null)

  const openForm = (mode: 'new' | 'edit' | 'copy') => {
    if (!canEdit) return
    if ((mode === 'edit' || mode === 'copy') && !selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    setFormState({ mode, doId: mode === 'new' ? null : selectedRow?.id ?? null })
  }

  const { data: deleteAccess } = useGetSaleDeliveryOrderCanHardDeleteQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })
  const canHardDelete = canEdit && (deleteAccess?.canDelete ?? false)
  const [deleteSaleDeliveryOrder, { isLoading: deleting }] = useDeleteSaleDeliveryOrderMutation()
  // Print — SA (BL No): Access rpCtrlSalesSA master-detail report preview.
  const [triggerSaPreview] = useLazyGetSaPrintPreviewQuery()
  const [saPrint, setSaPrint] = useState<{ open: boolean; title: string; html: string; loading: boolean }>({
    open: false,
    title: '',
    html: '',
    loading: false,
  })
  const [triggerDoPreview] = useLazyGetDoPrintPreviewQuery()
  const [doPrint, setDoPrint] = useState<{ open: boolean; title: string; html: string; loading: boolean }>({
    open: false,
    title: '',
    html: '',
    loading: false,
  })
  const [triggerDispatchForm, { isFetching: dispatchFormLoading }] = useLazyGetDispatchPrintFormQuery()
  const [triggerDispatchPreview] = useLazyGetDispatchPrintPreviewQuery()
  // cp_code of the selected row's contract — gates Print-Dispatch (docs key by cp_code).
  // null = unresolvable (inactive/unassigned contract) → button disabled.
  const [dispatchCpCode, setDispatchCpCode] = useState<string | null>(null)
  const [dispatchEdit, setDispatchEdit] = useState<{ open: boolean; doNo: string | null }>({ open: false, doNo: null })
  const [dispatchPrint, setDispatchPrint] = useState<{ open: boolean; title: string; html: string; loading: boolean }>({
    open: false,
    title: '',
    html: '',
    loading: false,
  })
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const { getWidth, startResize } = useColumnWidths('sale-delivery-order.list')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof SaleDeliveryOrder> | null>(null)

  const filterPopover = useFilterPopover<FilterKind>()
  const closeFilterPopover = () => filterPopover.close()
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => filterPopover.open(kind, e)

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof SaleDeliveryOrder
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    closeFilterPopover()
  }

  const columnsCtrl = usePreferenceColumns({
    scope: 'sale_control',
    prefKey: 'columns',
    subKey: 'delivery-order',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'sale-delivery-order.list',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const {
    data: list,
    isLoading,
    isFetching,
    error,
  } = useGetSaleDeliveryOrdersQuery(
    {
      page: 1,
      pageSize: ALL_ROWS_PAGE_SIZE,
      ...(shipmentPeriodStart ? { shipmentPeriodFrom: shipmentPeriodStart } : {}),
      ...(shipmentPeriodEnd ? { shipmentPeriodTo: shipmentPeriodEnd } : {}),
      finalizedFilter,
    },
    { refetchOnMountOrArgChange: true },
  )

  const baseRows = useMemo(() => list?.items ?? [], [list])
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])
  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter((col) => col.kind === 'number' && col.key !== 'id')
          .map((col) => [col.key, sumNumericColumn(filteredRows, col.key)] as const),
      ),
    [filteredRows, visibleColumns],
  )
  const popoverColumn = useMemo<ColumnDef | null>(() => {
    if (!colPopover) return null
    return TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null
  }, [colPopover])
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, String(colPopover.key)) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  const idRangeText = useMemo(() => {
    if (rows.length === 0) return 'ID: —'
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    for (const r of rows) {
      const id = Number(r.id)
      if (!Number.isFinite(id)) continue
      if (id < min) min = id
      if (id > max) max = id
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return 'ID: —'
    return `ID: ${min}–${max}`
  }, [rows])
  const minTableWidth = Math.max(960, visibleColumns.length * 96)
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: keyof SaleDeliveryOrder) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo<SaleDeliveryOrder | null>(
    () => rows.find((r) => r.id === selectedId) ?? null,
    [rows, selectedId],
  )

  // Resolve cp_code for the selected row (same source as dispatch save). Drives the
  // Print-Dispatch disabled state: no cp_code (inactive/unassigned contract) → can't
  // save shipping docs → button disabled. Re-runs when the selected DO changes.
  const selectedDoNo = selectedRow?.doNo ?? ''
  useEffect(() => {
    if (!selectedDoNo) {
      setDispatchCpCode(null)
      return
    }
    let cancelled = false
    setDispatchCpCode(null)
    void triggerDispatchForm(selectedDoNo).unwrap()
      .then((form) => { if (!cancelled) setDispatchCpCode(form.cpCode ?? null) })
      .catch(() => { if (!cancelled) setDispatchCpCode(null) })
    return () => { cancelled = true }
  }, [selectedDoNo, triggerDispatchForm])

  // Access btnPrintDO/SA/Dispatch all guard on `IsNull(do_no) = False` ("Delivery
  // order no must be not null!"). A selected row without a DO No. cannot be printed,
  // so the Print buttons are disabled for it (mirrors the Access MsgBox guard).
  const canPrintSelected = !!selectedRow && !!selectedRow.doNo
  // Print-SA (Access btnPrintSAAll) additionally filters by sale_contract + b_l_number
  // (rowCount = 0 → "No data to print!"). Disable when either is missing on the row.
  const canPrintSa =
    canPrintSelected && !!selectedRow?.saleContract && !!(selectedRow?.blNumber ?? '').replace(/\s/g, '')

  // New / Edit / Copy open `frmCtrlSalesQryDOAdd`; Print buttons open Access reports
  // (rpCtrlSalesSA / rpCtrlSalesDO / dispatch). Those Access objects are separate
  // documents — not yet converted, so the actions surface a notice for now.
  // Access btnPrintDO_Click: open rpCtrlSalesDO for the selected row's do_no.
  const handlePrintDo = async () => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    if (!selectedRow.doNo) {
      snack.warning('This record has no DO number to print.')
      return
    }
    setDoPrint({ open: true, title: `Print Preview - DO (${selectedRow.doNo})`, html: '', loading: true })
    try {
      const res = await triggerDoPreview(selectedRow.doNo).unwrap()
      setDoPrint({ open: true, title: res.title, html: res.html, loading: false })
    } catch (err) {
      const e = err as { status?: number; data?: { detail?: string } }
      snack.warning(e?.status === 422 ? e.data?.detail || 'Cannot print this record' : 'Failed to generate print preview.')
      setDoPrint({ open: false, title: '', html: '', loading: false })
    }
  }

  // Access btnPrintDispatch_Click: open the editable frmCtrlSalesDispatchPrint form
  // (shipping-document list). Print there persists docs + renders rpCtrlSalesDispatch.
  // Shipping docs are keyed by the contract's client code (cp_code). The Print-Dispatch
  // button is disabled when cp_code is unresolvable (contract inactive / unassigned),
  // resolved per selected row in the effect below — so the form only opens when it can save.
  const handlePrintDispatch = async () => {
    if (!selectedRow?.doNo || !dispatchCpCode) return
    // Account chỉ View: bỏ qua form edit shipping docs, mở thẳng Print Preview
    // read-only (GET /dispatch-print/preview — không ghi DB). Nút Print (in thật)
    // trong DispatchPrintDialog tự ẩn khi !canEdit.
    if (!canEdit) {
      setDispatchPrint({ open: true, title: `Print Preview - Dispatch (${selectedRow.doNo})`, html: '', loading: true })
      try {
        const res = await triggerDispatchPreview(selectedRow.doNo).unwrap()
        setDispatchPrint({ open: true, title: res.title, html: res.html, loading: false })
      } catch (err) {
        const e = err as { status?: number; data?: { detail?: string } }
        snack.warning(e?.status === 422 ? e.data?.detail || 'Cannot print this record' : 'Failed to generate print preview.')
        setDispatchPrint({ open: false, title: '', html: '', loading: false })
      }
      return
    }
    setDispatchEdit({ open: true, doNo: selectedRow.doNo })
  }

  // Access btnPrintSAAll_Click: open rpCtrlSalesSA for the selected row's
  // Shipping Advice (sale_contract + b_l_number). BE renders master-detail HTML.
  const handlePrintSa = async () => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    const saleContract = selectedRow.saleContract ?? ''
    const blNumber = selectedRow.blNumber ?? ''
    if (!saleContract || !blNumber.replace(/\s/g, '')) {
      snack.warning('This record has no sale contract / B/L number to print.')
      return
    }
    setSaPrint({ open: true, title: `Shipping Advice (${saleContract} / ${blNumber})`, html: '', loading: true })
    try {
      const result = await triggerSaPreview({ saleContract, blNumber }).unwrap()
      setSaPrint({ open: true, title: result.title, html: result.html, loading: false })
    } catch (err) {
      const e = err as { status?: number; data?: { detail?: string } }
      const msg = e?.status === 422 ? e.data?.detail || 'Cannot print this record' : 'Failed to generate print preview.'
      snack.warning(msg)
      setSaPrint({ open: false, title: '', html: '', loading: false })
    }
  }

  const handleDeleteClick = () => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    setConfirmDelete(selectedRow)
  }

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteSaleDeliveryOrder(confirmDelete.id).unwrap()
      snack.success(`Delivery Order ${confirmDelete.doNo ?? ''} deleted`)
      if (selectedId === confirmDelete.id) setSelectedId(null)
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete delivery order failed'))
    }
  }

  const cellValue = (row: SaleDeliveryOrder, col: ColumnDef): string => {
    const val = row[col.key]
    if (val == null) return ''
    if (col.kind === 'date') return formatAccessDate(val)
    if (col.kind === 'datetime') return formatAccessDateTime(val)
    if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
    if (typeof val === 'number') return formatAccessNumber(val)
    return String(val)
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button
              variant="contained"
              size="small"
              color="success"
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={() => openForm('new')}
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              title="New"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || !canEdit}
              onClick={() => openForm('edit')}
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Edit"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || !canEdit}
              onClick={() => openForm('copy')}
              startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
              title="Copy"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Copy</Box>
            </Button>
            {/* Button order follows Access `frmCtrlSalesMainSubFormDO` (Left order):
                New · Edit · Copy · Delete · Print-DO · Print-SA(BL No) · Print-Dispatch.
                btnPrintSA ("Print - SA") is Visible=NotDefault in Access → omitted. */}
            {canHardDelete && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                sx={actionToolbarBtnSx}
                disabled={!selectedRow || !canEdit}
                onClick={handleDeleteClick}
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                title="Delete"
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!selectedRow || !canPrintSelected}
              onClick={() => void handlePrintDo()}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Print - DO"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Print-</Box>DO
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!selectedRow || !canPrintSa}
              onClick={() => void handlePrintSa()}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Print - SA (BL No)"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Print-</Box>SA (BL No)
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!selectedRow || !canPrintSelected || dispatchFormLoading || !dispatchCpCode}
              onClick={() => void handlePrintDispatch()}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title={selectedRow && !dispatchFormLoading && !dispatchCpCode ? 'No client code — dispatch documents cannot be saved' : 'Print - Dispatch'}
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Print-</Box>Dispatch
            </Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => openFilterPopover('search', e)} />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load Delivery Orders.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              {idRangeText}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="DO no, contract, buyer..."
              label="Search delivery orders"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={(popoverKind === 'none' ? 'text' : popoverKind)}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load Delivery Orders"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading || isFetching ? (
            // Reload/refetch: RTK giữ data cũ + chỉ set isFetching → phải show
            // Loading (KHÔNG để bảng cũ đứng yên) tới khi API response mới về.
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No Delivery Order found"
              description="Try adjusting the saved shipment period, finalized filter, or the table filters above."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = 0
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          userSelect: 'text',
                          ...(isFrozen ? {} : { position: 'relative' }),
                        }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {visibleColumns.map((col, index) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const total = totalRowValues.get(col.key)
                    const val = total == null ? '' : formatAccessNumber(total)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: SaleDeliveryOrder) =>
                visibleColumns.map((col) => {
                  const key = String(col.key)
                  const isFrozen = FROZEN_KEYS.has(key)
                  const val = cellValue(row, col)
                  const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                  const wrap = col.wrap || getWidth(key) != null
                  if (wrap) {
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-wrap">
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                    >
                      <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const rowId = Number.isFinite(idx) ? rows[idx]?.id : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={rowId != null && rowId === selectedId}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (rowId == null) return
                        toggleSelect(rowId)
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </Box>
      </Box>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Delivery Order"
        message={confirmDelete ? `Do you want to delete Delivery Order No = '${confirmDelete.doNo ?? ''}' ?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
      {formState && (
        <DeliveryOrderFormDialog
          open
          mode={formState.mode}
          doId={formState.doId}
          canEdit={canEdit}
          onClose={() => setFormState(null)}
          onSaved={() => setSelectedId(null)}
        />
      )}
      <FuturesColumnsMenu controller={columnsCtrl} />
      <SaPrintDialog
        open={saPrint.open}
        title={saPrint.title}
        html={saPrint.html}
        loading={saPrint.loading}
        canEdit={canEdit}
        onClose={() => setSaPrint({ open: false, title: '', html: '', loading: false })}
      />
      <DoPrintDialog
        open={doPrint.open}
        title={doPrint.title}
        html={doPrint.html}
        loading={doPrint.loading}
        canEdit={canEdit}
        onClose={() => setDoPrint({ open: false, title: '', html: '', loading: false })}
      />
      <DispatchEditDialog
        open={dispatchEdit.open}
        doNo={dispatchEdit.doNo}
        canEdit={canEdit}
        onClose={() => setDispatchEdit({ open: false, doNo: null })}
        onPrinted={(title, html) => setDispatchPrint({ open: true, title, html, loading: false })}
      />
      <DispatchPrintDialog
        open={dispatchPrint.open}
        title={dispatchPrint.title}
        html={dispatchPrint.html}
        loading={dispatchPrint.loading}
        canEdit={canEdit}
        onClose={() => setDispatchPrint({ open: false, title: '', html: '', loading: false })}
      />
    </Box>
  )
}
